import { useCallback, useEffect, useState } from 'react';
import { tendApi, DesignElementLibraryItem } from '../../../api/tendApi';

export interface UseDesignElementsLibraryResult {
  elements: DesignElementLibraryItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  upload: (args: {
    file: File;
    scope: 'universal' | 'brand';
    name?: string;
  }) => Promise<DesignElementLibraryItem | null>;
  remove: (id: number) => Promise<boolean>;
}

/**
 * Loads the design-element catalog visible to the current brand
 * (universal + brand-scoped). brandId may be null/undefined to load only universal.
 */
export function useDesignElementsLibrary(brandId?: number | null): UseDesignElementsLibraryResult {
  const [elements, setElements] = useState<DesignElementLibraryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await tendApi.listDesignElements(brandId ?? null);
      setElements(next);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { void refetch(); }, [refetch]);

  const upload = useCallback(
    async (args: { file: File; scope: 'universal' | 'brand'; name?: string }) => {
      if (args.scope === 'brand' && brandId == null) {
        throw new Error('brandId required for scope="brand"');
      }
      const created = await tendApi.uploadDesignElement({
        file: args.file,
        scope: args.scope,
        brandId: args.scope === 'brand' ? (brandId as number) : undefined,
        name: args.name,
      });
      if (created) {
        setElements((prev) => [created, ...prev]);
      }
      return created;
    },
    [brandId],
  );

  const remove = useCallback(async (id: number) => {
    const ok = await tendApi.deleteDesignElement(id);
    if (ok) setElements((prev) => prev.filter((e) => e.id !== id));
    return ok;
  }, []);

  return { elements, loading, error, refetch, upload, remove };
}
